import moment from "moment"
import {
  assignAttributeStateToTLLegends,
  convertTimeToMinutes,
  generateLegend,
  getMergedYAxisList,
  getPropertyAxisAndIsAdded,
  getTimeIndex,
  intervalToMinutes,
  legendIDGenerator,
  legendName
} from "../common"
import { isArray, isString, toNumber } from "lodash"
import {
  chartDateFormat,
  constantValues,
  reduxStoreTimeStampFormat
} from "../../constant"
import { DOT_SHAPE, XS_SIZE, LEVEL_ONE } from "src/blitz/Charts/constants"
import {
  TIMELINE_PROPERTY_TYPE,
  transformTimeLinechartNew
} from "./timeLineChart"
import { NONE } from "src/blitz/Charts/constants"
import { generateExceptionBars } from "../Exceptions/index"
import { generateExceptionLegends } from "../Exceptions/helper"
import { generateSuppressionLegends } from "../Suppressions/helper"
import { generateSuppressionBars } from "../Suppressions"

export const EXPECTED_VALUES = "Expected Values"
export const UNEXPECTED_VALUES = "Unexpected Values"
const scatterWithLimitsLegends = [UNEXPECTED_VALUES, EXPECTED_VALUES]
export const transformScatterWithLimitchart = ({
  tz,
  startDate,
  endDate,
  intervalMinutes = "15m",
  inputData,
  equipmentId,
  chartInfo,
  equipmentList,
  addedProperties,
  assignedAttributes,
  legendAttributesCombination,
  oldTimeLineLegends = null
}) => {
  let calculatedMin = intervalToMinutes(intervalMinutes)
  let minimumCalculatedMin = intervalToMinutes(intervalMinutes)
  let minInterval = calculatedMin
  const defaultCalculatedMin = intervalToMinutes(intervalMinutes)
  let lastIndexPosition = (60 / calculatedMin) * 24 - 1
  const equipmentType = chartInfo?.equipmentType
  const chartIsEconomizerOutdoorAirFraction =
    chartInfo?.title === "Economizer Outdoor Air Fraction"
  console.log("chartInfo:", chartInfo)
  const legends = []
  let timeLineLegends = {}
  const addedAxisList = addedProperties?.axisList || []
  const data = inputData?.data || {}
  const newData = {}
  const newDataTimeline = {}
  const allTimestampsObject = {}
  const localTimeStamp = Boolean(tz)
    ? moment(
        moment.tz(moment(), tz).format("MM-DD-YYYY HH:mm"),
        "MM-DD-YYYY HH:mm"
      )
    : moment()
  const endTimestamp = moment(endDate, chartDateFormat).add(1, "days")
  for (const id in data) {
    const equipmentName = equipmentList?.find((obj) => obj?.id === id)?.name
    const selectedEquipment = equipmentId === id
    const paramData = data[id]
    const properties = paramData?.properties
    const sortedDates = Object.keys(paramData?.values || {})
      .filter((date) => date != "frq")
      .sort(
        (a: string, b: string) =>
          new Date(a).getTime() - new Date(b).getTime()
      )

    scatterWithLimitsLegends?.forEach((legendText, i) => {
      const legendId = legendIDGenerator(id, legendText)
      const legendObj = {
        id: legendId,
        name: `${legendText}`,
        color: `#${i === 0 ? "d31d2e" : "00b816"}`,
        axisName: "y",
        shapeSize: chartIsEconomizerOutdoorAirFraction ? XS_SIZE : LEVEL_ONE,
        shape: DOT_SHAPE,
        show: true,
        visible: true,
        seriesKey: id
      }
      legends.push(legendObj)
    })

    for (const propertyKey in properties) {
      calculatedMin = defaultCalculatedMin
      minInterval = defaultCalculatedMin
      lastIndexPosition = (60 / minInterval) * 24 - 1
      const property = properties[propertyKey]
      const propertyIndex = property?.index
      const propertyName = property?.name || ""

      const { axis, isUserAddedProperty, legendId } =
        getPropertyAxisAndIsAdded({
          chartInfo,
          propertyKey,
          equipmentType,
          equipmentList,
          id,
          addedAxisList
        })

      if (axis?.axisName?.toLowerCase() === "x") {
        const legendId = legendIDGenerator(id, "xAxis")
        const name = legendName({ propertyName }, [true, false])
        const legendObj = {
          id: legendId,
          name: name,
          color: "",
          axisName: "x",
          shapeSize: chartIsEconomizerOutdoorAirFraction ? XS_SIZE : LEVEL_ONE,
          shape: NONE,
          show: true,
          visible: true,
          disabled: true
        }
        legends.push(legendObj)
      }

      if (axis?.axisName?.toLowerCase() === "timeline") {
        const name = legendName({
          selectedEquipment,
          propertyName,
          equipmentName
        })
        const legendObj = generateLegend({
          legendId,
          name,
          axisName: axis?.axisName,
          symbol: axis?.symbol,
          propertyKey,
          type: "timeline",
          isUserAddedProperty,
          oldLegends: oldTimeLineLegends,
          propertyType: TIMELINE_PROPERTY_TYPE
        })
        timeLineLegends[legendId] = legendObj
      }

      let currentTimestamp = moment(startDate, chartDateFormat).locale("en")

      let prevValue = null
      let loop = 0
      while (
        currentTimestamp.isSameOrBefore(endTimestamp) &&
        currentTimestamp.isSameOrBefore(localTimeStamp)
      ) {
        const dateKey = currentTimestamp.format("YYYY-MM-DD")
        const extractTime = currentTimestamp.format("HH:mm")
        const timetoMin = convertTimeToMinutes(extractTime)

        const dateAvailable = paramData?.values?.[dateKey]
        if (dateAvailable) {
          const dateIndexPosition = sortedDates.indexOf(dateKey)
          const extractedInterval =
            paramData.values?.frq?.[dateIndexPosition]?.split("|")?.[
              propertyIndex
            ]
          calculatedMin = extractedInterval / 60 || defaultCalculatedMin
          minInterval =
            paramData?.values?.[dateKey]?.["intervalInSeconds"] / 60 ||
            defaultCalculatedMin
          lastIndexPosition = (60 / minInterval) * 24 - 1

          if (calculatedMin < minimumCalculatedMin) {
            minimumCalculatedMin = calculatedMin
          }
        }

        const timeIndexPosition = getTimeIndex(timetoMin, minInterval)

        let finalValue
        if (dateAvailable) {
          const valueArray = paramData?.values?.[dateKey]?.[timeIndexPosition]
          const valueArrayIsArray = isArray(valueArray)
          const arrayValues = valueArrayIsArray ? valueArray : []
          let value = arrayValues[propertyIndex]

          const isOutOfRange = value?.toString()?.startsWith("_")
          if (isOutOfRange && value?.toString()?.length > 1) {
            value = value?.toString()?.substring(1)
          }

          const isNull = constantValues?.includes(value)
          const isTimeline = axis?.axisName === "timeline"

          const checkedValue =
            isNull || (isTimeline ? false : !isFinite(toNumber(value)))
              ? null
              : isTimeline
              ? value
              : parseFloat(value)

          if ((isString(value) && value?.length === 0) || !valueArrayIsArray) {
            finalValue = prevValue
          } else {
            finalValue = checkedValue
          }
          if (timeIndexPosition === lastIndexPosition) {
            prevValue = null
          } else {
            prevValue = finalValue
          }
        } else {
          finalValue = null
          prevValue = null
        }

        const dateVal = currentTimestamp.format(reduxStoreTimeStampFormat)
        const legendId = legendIDGenerator(id, propertyKey)
        if (axis.axisName === "timeline") {
          if (!newDataTimeline?.[legendId]) {
            newDataTimeline[legendId] = {
              data: [],
              timestamps: []
            }
          }
        } else {
          if (!newData?.[id]) {
            newData[id] = {
              data: [],
              timestamps: []
            }
          }
        }
        if (!allTimestampsObject[dateVal]) {
          allTimestampsObject[dateVal] = 1
        }
        if (axis?.axisName === "y" || axis?.axisName === "x") {
          if (!newData?.[id].data?.[loop]) {
            newData[id].data[loop] = {}
          }
          newData[id].data[loop][axis?.axisName] = finalValue
          newData[id].timestamps.push(dateVal)
        } else if (axis?.axisName === "timeline") {
          newDataTimeline[legendId].data.push(finalValue)
          newDataTimeline[legendId].timestamps.push(dateVal)
        }
        currentTimestamp = currentTimestamp.add(calculatedMin, "minutes")
        loop++
      }
    }
  }

  const allTimestamps = Object.keys(allTimestampsObject).sort()
  const noDataArray = Object.keys(newData)?.map((key) => {
    const data = newData?.[key]?.data || []
    const noData = data?.every((e) =>
      Object.values(e || {})?.every((e) => !Boolean(e))
    )
    return noData
  })

  const isNoData = noDataArray?.every((e) => Boolean(e))

  // Create/update Exception Legends
  const exceptionData = inputData?.exceptions?.data || {}
  const exceptionLegends = generateExceptionLegends({
    exceptionData,
    chartInfo,
    equipmentList,
    selectedEquipID: equipmentId,
    oldTimeLineLegends
  })

  // Create/update Suppression Legends
  const suppressionData = inputData?.exceptions?.suppressed?.data || {}
  const suppressionLegends = generateSuppressionLegends({
    suppressionData,
    chartInfo,
    oldTimeLineLegends
  })

  // Assign Colors to new time line legends of all kinds
  assignAttributeStateToTLLegends(
    timeLineLegends,
    exceptionLegends,
    suppressionLegends,
    assignedAttributes,
    legendAttributesCombination,
    oldTimeLineLegends
  )

  const { timeLineBars, totalLanes } = transformTimeLinechartNew(
    timeLineLegends,
    newDataTimeline
  )

  const { exceptionsBars, exceptionLanes } = generateExceptionBars(
    exceptionData,
    exceptionLegends,
    chartInfo,
    totalLanes,
    startDate,
    endTimestamp,
    localTimeStamp
  )

  const suppressed =
    (inputData?.exceptions?.suppressed?.data &&
      inputData?.exceptions?.suppressed?.data[equipmentId]) ||
    {}
  const { suppressionBars } = generateSuppressionBars(
    suppressed,
    suppressionLegends,
    exceptionLanes,
    startDate,
    endTimestamp.format("YYYY-MM-DD")
  )

  const bars = [...timeLineBars, ...exceptionsBars, ...suppressionBars]

  const noDataTimelineArray = Object.keys(timeLineLegends)?.map((key) => {
    const noData = (newDataTimeline?.[key]?.data || [])?.every(
      (e) => !Boolean(e) && e !== 0
    )
    return noData
  })

  const isNoDataTimeline = noDataTimelineArray?.every((e) => Boolean(e))

  const isNoTimeLineData = timeLineBars?.every((obj) => !Boolean(obj?.value))

  timeLineLegends = {
    ...timeLineLegends,
    ...exceptionLegends,
    ...suppressionLegends
  }

  const axisList = getMergedYAxisList(chartInfo, addedAxisList)

  return {
    chartData: {
      data: isNoData ? {} : newData,
      timelineData: isNoDataTimeline ? {} : newDataTimeline
    },
    chartProps: {
      legends: legends,
      timestamps: allTimestamps,
      axisList: axisList,
      timeLineLegends: timeLineLegends,
      timeLineBars:
        isNoData && isNoDataTimeline && isNoTimeLineData ? [] : bars,
      noDataAvailable: isNoData && isNoTimeLineData ? true : false,
      isdataprocessed: true,
      calculatedInterval: minimumCalculatedMin
    }
  }
}
